import React, { useState, useEffect } from 'react';
import config from '../../Common/Configurations/APIConfig';
import apiService from "../../Common/Configurations/APIService";
import Header from '../../Common/Layout/Header';
import { useAuth } from '../../Common/Configurations/AuthContext';
import Modal from 'react-bootstrap/Modal';
import { Link, useNavigate } from 'react-router-dom';
import DeleteAlert from '../../Common/Layout/DeleteAlert';

const initialFieldValues = {
    faqId: "00000000-0000-0000-0000-000000000000",
    faqCategoryId: "",
    faqQuestion: "",
    faqAnswer: ""
};

const Faq = () => {
    const navigate = useNavigate();
    const { user, loading } = useAuth();
    
    // --- State Variables ---
    const [lists, setLists] = useState([]);          // For FAQ Table
    const [categoryLists, setCategoryLists] = useState([]); // For Dropdown
    
    const [btnLoading, setBtnLoading] = useState(false);
    const [values, setValues] = useState(initialFieldValues);
    const [errors, setErrors] = useState({});
    const [recordForEdit, setRecordForEdit] = useState(null);
    
    // Pagination
    const [totalPages, setTotalPages] = useState(0);
    const [pageSize, setPageSize] = useState(20);
    const [pageNumber, setPageNumber] = useState(1);
    const [data, setData] = useState({});
    const [totalRecords, setTotalRecords] = useState(0);
    
    // Modals
    const [addShow, setAddShow] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [itemToDelete, setItemToDelete] = useState(null);

    // --- Modal Handlers ---
    const handleOpenDeleteModal = (id) => {
        setItemToDelete(id);
        setShowDeleteModal(true);
    };

    const handleCloseDeleteModal = () => {
        setShowDeleteModal(false);
        setItemToDelete(null);
    };

    const handleAddClose = () => {
        setAddShow(false);
        setRecordForEdit(null);
    };

    const handleAddShow = () => {
        setRecordForEdit(null);
        setValues(initialFieldValues);
        setAddShow(true);
    };

    useEffect(() => {
        if (recordForEdit !== null) {
            setValues(recordForEdit);
        }
    }, [recordForEdit]);

    // ---------------------------------------------------
    // 1. GET FAQ DATA (Populate Table)
    // ---------------------------------------------------
    const GetData = async (pageNumber) => {
        try {
            // Make sure GETALLFAQ is added to your APIConfig.js
            const res = await apiService.getAll(`${config.GETALLFAQ}`); 
            console.log("FAQ Response:", res);

            let finalList = [];
            let finalTotalRecords = 0;

            // Robust check: Array vs Object vs Nested Data
            if (Array.isArray(res)) {
                finalList = res;
                finalTotalRecords = res.length;
                setPageNumber(1);
                setTotalPages(1);
                setData({ previousPage: null, nextPage: null });
            } 
            else if (res?.data && Array.isArray(res.data)) {
                finalList = res.data;
                finalTotalRecords = res.totalRecords || res.data.length;
                setPageNumber(res.pageNumber || 1);
                setPageSize(res.pageSize || 20);
                setTotalPages(res.totalPages || 1);
                setData(res);
            }
            else if (res?.data?.data && Array.isArray(res.data.data)) {
                finalList = res.data.data;
                finalTotalRecords = res.data.totalRecords || res.data.data.length;
                setData(res.data);
            }

            setLists(finalList);
            setTotalRecords(finalTotalRecords);

        } catch (error) {
            console.error("Error fetching FAQs:", error);
            setLists([]);
        }
    };

    // ---------------------------------------------------
    // 2. GET CATEGORY DATA (Populate Dropdown)
    // ---------------------------------------------------
    const GetCategoryData = async () => {
        try {
            // Using the key you provided in the chat
            const res = await apiService.getAll(`${config.GETALLFAQCATEGORY}`);
            
            let cats = [];
            // Same robust check for the dropdown data
            if (Array.isArray(res)) cats = res;
            else if (res?.data && Array.isArray(res.data)) cats = res.data;
            else if (res?.data?.data && Array.isArray(res.data.data)) cats = res.data.data;

            setCategoryLists(cats);
        } catch (error) {
            console.error("Error fetching Categories:", error);
            setCategoryLists([]);
        }
    };

    // --- Pagination Helpers ---
    const GetLastPageData = () => { GetData(totalPages); }
    const GetFirstPageData = () => { GetData("1"); }
    const GetPageData = (number) => {
        setPageNumber(number);
        if (pageNumber !== number) GetData(number);
    }

    const pageNumbers = [];
    for (let i = 1; i <= totalPages; i++) { pageNumbers.push(i); }

    const renderPageNumbers = pageNumbers.map((number) => {
        return (
            <li className="page-item" key={number} id={number} onClick={() => GetPageData(number)}>
                <Link className="page-link">{number}</Link>
            </li>
        );
    });

    // --- CRUD Operations ---
    const showEditDetails = (data) => {
        setRecordForEdit(data);
        setValues(data);
        setAddShow(true);
    };

    const handleDelete = async () => {
        if (itemToDelete !== null) {
            try {
                // Ensure DELETEFAQ is in APIConfig.js
                await apiService.remove(config.DELETEFAQ, itemToDelete);
                await GetData(pageNumber);
            } catch (error) {
                console.error(error);
            }
            handleCloseDeleteModal();
        }
    }

    const validate = () => {
        let temp = {};
        // Validate inputs are not empty
        temp.faqCategoryId = values.faqCategoryId && values.faqCategoryId !== "00000000-0000-0000-0000-000000000000" ? true : false;
        temp.faqQuestion = values.faqQuestion ? true : false;
        temp.faqAnswer = values.faqAnswer ? true : false;
        setErrors(temp);
        return Object.values(temp).every((x) => x === true);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (validate()) {
            setBtnLoading(true);
            var formData = {
                "faqId": values.faqId,
                "faqCategoryId": values.faqCategoryId,
                "faqQuestion": values.faqQuestion,
                "faqAnswer": values.faqAnswer
            }
            try {
                const isNew = formData.faqId === "00000000-0000-0000-0000-000000000000";

                if (isNew) {
                    // Ensure CREATEFAQ is in APIConfig.js
                    await apiService.create(config.CREATEFAQ, formData);
                } else {
                    // Ensure UPDATEFAQ is in APIConfig.js
                    await apiService.update(config.UPDATEFAQ, formData);
                }
                await GetData(pageNumber);
                resetForm();

            } finally {
                setBtnLoading(false);
            }
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setValues({
            ...values,
            [name]: value
        });
    };

    const resetForm = () => {
        setValues(initialFieldValues);
        setAddShow(false);
        setBtnLoading(false);
    };

    const applyErrorClass = (field) => field in errors && errors[field] === false ? " form-control-danger" : "";

    // --- Initial Load ---
    useEffect(() => {
        if (user && !loading) {
            GetData(pageNumber);
            GetCategoryData(); // Fetch categories for the dropdown
        }
    }, [user, loading]);

    if (loading) return <p>Loading...</p>;
    if (!user) { navigate("/"); }

    return (
        <div id="layout-wrapper">
            <Header User={user} />
            <div className="main-content" style={{ marginTop: '70px', padding: '0 15px' }}>
                <div className="page-content">
                    <div className="container-fluid">
                        <div className="col-xl-12">
                            <div className="card">
                                <div className="p-2"></div>
                                <div className="me-2 pb-1">
                                    <div className="row g-4 align-items-center">
                                        <div className="col-sm-auto ms-auto">
                                            <div className="hstack gap-2">
                                                <button type="button" className="btn btn-primary add-btn" onClick={handleAddShow} >
                                                    <i className="ri-add-line align-bottom me-1" /> Add FAQ
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="card-body">
                                    <div className="table-responsive table-card p-2">
                                        <table className="table align-middle" id="faqTable">
                                            <thead className="bg-soft-success bg-gradient">
                                                <tr>
                                                    <th>Id</th>
                                                    <th>Category</th>
                                                    <th>Question</th>
                                                    <th>Answer</th>
                                                    <th>Action</th>
                                                </tr>
                                            </thead>
                                            <tbody className="list form-check-all">
                                                {/* Safety Check: ensure lists is not undefined */}
                                                {lists && lists.length > 0 ? lists.map((v, index) =>
                                                    <tr key={v.faqId}>
                                                        <td>{index + 1}</td>
                                                        {/* Optional chaining to prevent crash if faqCategory is null */}
                                                        <td>{v.faqCategory ? v.faqCategory.faqCategoryName : 'No Category'}</td>
                                                        <td>{v.faqQuestion}</td>
                                                        {/* Limit answer width so table doesn't break */}
                                                        <td style={{maxWidth:'300px', whiteSpace:'pre-wrap'}}>{v.faqAnswer}</td>
                                                        <td>
                                                            <ul className="list-inline hstack gap-2 mb-0">
                                                                <li className="list-inline-item" title="Edit">
                                                                    <Link className="edit-item-btn" onClick={e => showEditDetails(v)}>
                                                                        <i className="ri-pencil-fill align-bottom text-muted" />
                                                                    </Link>
                                                                </li>
                                                                <li className="list-inline-item" title="Delete">
                                                                    <Link className="remove-item-btn" onClick={e => handleOpenDeleteModal(v.faqId)}>
                                                                        <i className="ri-delete-bin-fill align-bottom text-muted" />
                                                                    </Link>
                                                                </li>
                                                            </ul>
                                                        </td>
                                                    </tr>
                                                ) : (
                                                    <tr><td colSpan="5" className="text-center">No Records Found</td></tr>
                                                )}
                                            </tbody>
                                        </table>
                                    </div>
                                    <div className="align-items-center mt-4 pt-2 justify-content-between d-flex">
                                        <div className="flex-shrink-0">
                                            <div className="text-muted">
                                                Showing <span className="fw-semibold">{lists ? lists.length : 0}</span> of <span className="fw-semibold">{totalRecords}</span> Results
                                            </div>
                                        </div>
                                        <ul className="pagination pagination-separated pagination-sm mb-0">
                                            <li className={"page-item" + (data && data.previousPage === null ? ' disabled' : '')} onClick={() => GetFirstPageData()}>
                                                <Link className="page-link">Previous</Link>
                                            </li>
                                            {renderPageNumbers}
                                            <li className={"page-item" + (data && data.nextPage === null ? ' disabled' : '')} onClick={() => GetLastPageData()}>
                                                <Link className="page-link">Next</Link>
                                            </li>
                                        </ul>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* --- ADD/EDIT MODAL --- */}
                <Modal show={addShow} onHide={handleAddClose} size="lg">
                    <Modal.Header>
                        <Modal.Title>{values.faqId === "00000000-0000-0000-0000-000000000000" ? "Add" : "Edit"} FAQ</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <form onSubmit={handleSubmit} autoComplete="off" noValidate>
                            <div className="row">
                                
                                {/* 1. Category Dropdown */}
                                <div className="col-lg-12 mb-3">
                                    <label htmlFor="faqCategoryId" className="form-label">Category</label>
                                    <select 
                                        name="faqCategoryId" 
                                        value={values.faqCategoryId} 
                                        onChange={handleInputChange} 
                                        className={"form-select" + applyErrorClass('faqCategoryId')}
                                    >
                                        <option value="">-- Select Category --</option>
                                        {categoryLists.map((item) => (
                                            <option key={item.faqCategoryId} value={item.faqCategoryId}>
                                                {item.faqCategoryName}
                                            </option>
                                        ))}
                                    </select>
                                    {errors.faqCategoryId === false ? (<div className="validationerror">Please select a category</div>) : ('')}
                                </div>

                                {/* 2. Question Input */}
                                <div className="col-lg-12 mb-3">
                                    <label htmlFor="faqQuestion" className="form-label">Question</label>
                                    <input 
                                        type="text" 
                                        value={values.faqQuestion} 
                                        name="faqQuestion" 
                                        onChange={handleInputChange} 
                                        className={"form-control" + applyErrorClass('faqQuestion')} 
                                        placeholder="Enter FAQ Question" 
                                    />
                                    {errors.faqQuestion === false ? (<div className="validationerror">Please enter question</div>) : ('')}
                                </div>

                                {/* 3. Answer Textarea */}
                                <div className="col-lg-12 mb-3">
                                    <label htmlFor="faqAnswer" className="form-label">Answer</label>
                                    <textarea 
                                        rows="4"
                                        value={values.faqAnswer} 
                                        name="faqAnswer" 
                                        onChange={handleInputChange} 
                                        className={"form-control" + applyErrorClass('faqAnswer')} 
                                        placeholder="Enter FAQ Answer" 
                                    />
                                    {errors.faqAnswer === false ? (<div className="validationerror">Please enter answer</div>) : ('')}
                                </div>

                                <Modal.Footer>
                                    <div className="hstack gap-2 justify-content-end mb-3 mt-4">
                                        {
                                            btnLoading === false ?
                                                <button type="submit" className="btn btn-primary">
                                                    {values.faqId === "00000000-0000-0000-0000-000000000000" ? "Submit" : "Update"}
                                                </button>
                                                :
                                                <button type="submit" className="btn btn-primary" disabled>Please wait...</button>
                                        }
                                        <button type="button" className="btn btn-danger" onClick={resetForm}>Cancel</button>
                                    </div>
                                </Modal.Footer>
                            </div>
                        </form>
                    </Modal.Body>
                </Modal>
                <DeleteAlert show={showDeleteModal} handleClose={handleCloseDeleteModal} handleDelete={handleDelete} />
            </div>
        </div>
    );
};

export default Faq;